import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  catchError,
  EMPTY,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  concatMap,
  tap,
} from 'rxjs';

import { EffectsBase, ReferenceDataFacade } from '@ps/core-data-access';
import { LoadingService } from '@ps/core-loading';
import { ToastService } from '@ps/core-overlay';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { SupplierService, SurveyService } from '@ps/survey-data-access';
import {
  buildSuppliersMetaDataFromSurvey,
  FeasibilityService,
} from '@ps/survey-creation-data-access';
import {
  activeSupplierIds,
  mapSurveyLocalization,
  resolveMasterLanguageCode,
  type FeasibilitySupplierBreakdown,
  type RateCards,
  type SupplierAllocation,
  type SupplierAllocationsEditableContext,
  type Survey,
} from '@ps/survey-utils';

import { toFeasibilityParamsFromSurvey } from '../domain/to-feasibility-params-from-survey';
import {
  ManageSupplierActions,
  ManageSupplierApiActions,
} from './manage-supplier.actions';

@Injectable()
export class ManageSupplierEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _surveyService = inject(SurveyService);
  private readonly _supplierService = inject(SupplierService);
  private readonly _feasibilityService = inject(FeasibilityService);
  private readonly _referenceData = inject(ReferenceDataFacade);
  private readonly _auth = inject(AuthSelectorsFacade);
  private readonly _toast = inject(ToastService);
  private readonly _loading = inject(LoadingService);

  // Loads the survey first (drawer scope, not page — the whole load belongs to
  // the drawer), then everything in parallel, then feasibility (needs the loaded
  // survey + active supplier ids). A manual 'drawer' token spans the whole chain
  // so the drawer's own overlay also covers the untagged feasibility POST — one
  // drawer loading end to end, no page overlay.
  public load$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSupplierActions.load),
      switchMap(({ surveyId, isChild }) => {
        const loadingId = this._loading.dispatch('drawer');
        // Child survey: fetch with child targeting params
        // (targeting=0&QBS=0&accessible=true); parent keeps targeting=1&QBS=1.
        const survey$ = isChild
          ? this._surveyService.getById(surveyId, 0, true, 'drawer')
          : this._surveyService.getByIdTargeting(surveyId, false, 'drawer');
        return survey$.pipe(
          switchMap((survey) => this._loadForSurvey(survey)),
          this.psCatchError(
            (error) =>
              of(
                ManageSupplierApiActions.loadFailure({
                  error: error.message ?? 'Failed to load supplier mix',
                }),
              ),
            { notify: true },
          ),
          finalize(() => this._loading.stop('drawer', loadingId)),
        );
      }),
    ),
  );

  // concatMap, not switchMap: an in-flight save must never be cancelled by a
  // second Apply — the user would be told it succeeded while it was dropped.
  public updateManagedAllocations$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSupplierActions.updateManagedAllocations),
      concatMap(({ surveyId, update }) =>
        this._supplierService.updateManagedAllocations(surveyId, update).pipe(
          map((allocations) =>
            ManageSupplierApiActions.updateAllocationsSuccess({
              allocations,
              blendId: update.context.blendId,
            }),
          ),
          this.psCatchError(
            (error) =>
              of(
                ManageSupplierApiActions.updateAllocationsFailure({
                  error: error.message ?? 'Failed to update suppliers',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  // concatMap: serialize saves so a second click never cancels an in-flight one.
  public saveBlend$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSupplierActions.saveBlend),
      concatMap(({ data }) =>
        this._supplierService.saveBlend(data).pipe(
          tap(() =>
            this._toast.success('Supplier blend saved successfully.', {
              keepOpen: false,
            }),
          ),
          map((blend) => ManageSupplierApiActions.saveBlendSuccess({ blend })),
          this.psCatchError(
            (error) =>
              of(
                ManageSupplierApiActions.saveBlendFailure({
                  error: error.message ?? 'Failed to save supplier blend',
                }),
              ),
            {
              notify: true,
              customMessage:
                'Unable to save this blend. Please refresh and try again.',
            },
          ),
        ),
      ),
    ),
  );

  // The saved blend carries its locale, so the list refetch reuses it — the new
  // blend then appears in the select alongside the rest.
  public reloadBlendsAfterSave$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSupplierApiActions.saveBlendSuccess),
      map(({ blend }) =>
        ManageSupplierActions.reloadBlends({ locale: blend.locale }),
      ),
    ),
  );

  public reloadBlends$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ManageSupplierActions.reloadBlends),
      switchMap(({ locale }) =>
        this._supplierService.blends(locale).pipe(
          map((blends) =>
            ManageSupplierApiActions.reloadBlendsSuccess({ blends }),
          ),
          // A failed refresh is minor — the blend already saved. Keep the
          // current list rather than wiping it.
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  private _loadForSurvey(survey: Survey): Observable<Action> {
    const locale = mapSurveyLocalization(
      survey.countryCode,
      survey.languageShortCode,
    );
    const editableContext: SupplierAllocationsEditableContext = {
      loi: survey.expectedLoi ?? 0,
      ir: survey.expectedIr ?? 0,
      languageCode: survey.languageCode ?? '',
      fieldTime: survey.fieldTime ?? 0,
      goal: survey.completesRequired ?? 0,
      clickBalancing: !!survey.clickBalancing,
    };

    return forkJoin({
      allocations: this._supplierService.allocations(survey.id, {
        scope: 'editable',
        context: editableContext,
      }),
      blends: this._supplierService.blends(locale),
      defaultAllocations:
        this._supplierService.defaultSupplierAllocation(locale),
      groupTemplates: this._supplierService.groupTemplates(),
      rateCards: this._loadRateCards(survey),
      flexibility: this._supplierService.flexibilityPriceParams(survey.id),
    }).pipe(
      switchMap((bundle) =>
        this._computeFeasibility(survey, bundle.allocations).pipe(
          map((feasibilitySuppliers) =>
            ManageSupplierApiActions.loadSuccess({
              survey,
              allocations: bundle.allocations,
              feasibilitySuppliers,
              blends: bundle.blends,
              groupTemplates: bundle.groupTemplates,
              defaultAllocations: bundle.defaultAllocations,
              rateCards: bundle.rateCards,
              suppliersMetaData: {
                ...buildSuppliersMetaDataFromSurvey(survey),
                manualOverride: bundle.flexibility.manualOverride,
                surveyPauseThreshold: bundle.flexibility.surveyPauseThreshold,
              },
            }),
          ),
        ),
      ),
    );
  }

  // Needs the master language code resolved from the countries list; when it
  // cannot be resolved (countries not yet loaded), the panel renders without
  // rate cards rather than hitting the endpoint with a wrong code.
  private _loadRateCards(survey: Survey): Observable<RateCards | null> {
    const countries = this._referenceData.countries();

    return resolveMasterLanguageCode(
      survey.countryCode,
      survey.languageShortCode,
      countries,
    )
      .map((languageCode) =>
        this._supplierService.rateCards({
          countryCode: survey.countryCode,
          languageCode,
          loi: survey.expectedLoi,
          incidence: survey.expectedIr,
          inclExcl: survey.inclExcl ?? 0,
          fieldTime: survey.fieldTime,
        }),
      )
      .unwrapOr(of<RateCards | null>(null));
  }

  private _computeFeasibility(
    survey: Survey,
    allocations: readonly SupplierAllocation[],
  ): Observable<readonly FeasibilitySupplierBreakdown[]> {
    return this._auth.user().match({
      some: (user) =>
        this._feasibilityService
          .compute(
            toFeasibilityParamsFromSurvey(
              survey,
              {
                id: user.id,
                email: user.email,
                companyId: this._auth.activeCompanyId(),
              },
              activeSupplierIds(allocations),
            ),
            { notifyUnusedQualifications: false },
          )
          .pipe(map((estimate) => estimate.suppliers)),
      none: () => of<readonly FeasibilitySupplierBreakdown[]>([]),
    });
  }
}
